import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from './Navbar';
import Footer from './Footer';
import DotBackground from './DotBackground';
import CookieConsent from './CookieConsent';
import MaterializeForm from './MaterializeForm';

const SyntheticPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const features = [
    {
      title: "High Quality Datasets",
      description: "High quality datasets simulating real sensors, including rare edge cases which is hard to get in the real life daily operations"
    },
    {
      title: "Synthetic Data Delivery",
      description: "Synthetic data delivery - streaming as if from real sensors MQTT, AI-based ETL pipelines to make the data ready for your systems. We go beyond just synthetic data for download and then work a lot to transform and cleanup the data."
    }
  ];

  const specificFeatures = [
    {
      title: "Physics & Chemistry Aware AI Models",
      description: "Data is generated based on physics and chemical aware AI Models, labelled and provided with reasoning documentation to trace it."
    },
    {
      title: "Flexible Data Delivery",
      description: "We also focus on data delivery, so data is available via MQTT real-time streams, file buckets or could be delivered directly to your infrastructure."
    },
    {
      title: "Human Support",
      description: "Human support - we are in contact if your team need engineering support on data side."
    }
  ];

  return (
    <div className="app-container">
      <DotBackground />
      <Navbar onOpenModal={() => setIsModalOpen(true)} />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden pt-16" id="hero">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-r from-accent/10 to-primary/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-secondary/5 to-accent/5 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
          >
            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-foreground mb-8 leading-tight font-mono">
              <span className="text-foreground">&gt;</span> Elevate Your IoT Development with{' '}
              <span className="text-foreground">
                Synthetic Time Series Datasets
              </span>
            </h1>
            
            {/* Description */}
            <div className="text-lg sm:text-xl text-foreground-secondary mb-10 max-w-4xl mx-auto leading-relaxed font-mono">
              <div className="mb-2">
                <span className="text-foreground-secondary">//</span> Unlock the power of realistic, high-fidelity IoT data without the limitations of real-world collection.
              </div>
              <div className="mb-2">
                <span className="text-foreground-secondary">//</span> As your dedicated IoT Synthetic Time Series Datasets Provider, we deliver premium datasets that mimic real sensors,
              </div>
              <div className="mb-2">
                <span className="text-foreground-secondary">//</span> complete with rare edge cases that are challenging to capture in daily operations.
              </div>
              <div className="mb-2">
                <span className="text-foreground-secondary">//</span> Go beyond static downloads — experience seamless synthetic data delivery through real-time streaming via MQTT,
              </div>
              <div>
                <span className="text-foreground-secondary">//</span> and AI-powered ETL pipelines that prepare the data precisely for your systems, in your preferred format and schema.
              </div>
            </div>

            {/* CTA Button */}
            <div className="flex justify-center mb-12">
              <motion.a 
                href="mailto:aol@simulatexp.dev"
                className="btn btn-primary btn-lg"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Contact us aol@simulatexp.dev
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Features Section */}
      <section className="section relative overflow-hidden bg-background-secondary">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-secondary/5 to-accent/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              Why Choose <span className="bg-gradient-to-r from-secondary to-accent bg-clip-text text-transparent">Us</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-20">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0, 0.2, 1] }}
              >
                <div className="card-elevated h-full p-8 group">
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-primary"></div>
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-4 group-hover:text-secondary transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-foreground-muted leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specific Features Section - Horizontal Boxes */}
      <section className="section relative overflow-hidden bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
              What We <span className="bg-gradient-to-r from-secondary to-accent bg-clip-text text-transparent">Offer</span>
            </h2>
          </motion.div>

          <div className="max-w-5xl mx-auto space-y-6">
            {specificFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0, 0.2, 1] }}
              >
                <div className="card-elevated p-6 group border-l-4 border-accent hover:border-primary transition-colors">
                  <div className="flex items-start gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                        <div className="w-8 h-8 rounded-full bg-primary"></div>
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-secondary transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-foreground-muted leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4, ease: [0.4, 0, 0.2, 1] }}
            className="text-center mt-16"
          >
            <motion.a 
              href="mailto:aol@simulatexp.dev"
              className="btn btn-primary btn-lg inline-flex"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Contact us aol@simulatexp.dev
            </motion.a>
          </motion.div>
        </div>
      </section>

      <Footer />
      <CookieConsent />
      <MaterializeForm isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default SyntheticPage;

